import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import TimeSlotServices from '../services/TimeSlotServices';
import useAsync from '../hooks/useAsync';
import Modal from 'react-modal';
import { toast } from 'react-toastify';

Modal.setAppElement('#root');

function Timeslots() {
    const { data: timeSlots, error, isLoading, run: refreshTimeSlots } = useAsync(TimeSlotServices.getTime);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [editForm, setEditForm] = useState({ startTime: '', endTime: '' });
    const [newSlotRange, setNewSlotRange] = useState({ startTime: '', endTime: '' });

    const sortedTimeSlots = useMemo(() => {
        if (!timeSlots) return [];
        
        return [...timeSlots].sort((a, b) => {
            const [aHours, aMinutes] = a.startTime.split(':').map(Number);
            const [bHours, bMinutes] = b.startTime.split(':').map(Number);
            
            if (aHours !== bHours) return aHours - bHours;
            return aMinutes - bMinutes;
        });
    }, [timeSlots]);

    const handleDeleteClick = (slot) => {
        setSelectedSlot(slot);
        setIsDeleteModalOpen(true);
    };

    const handleEditClick = (slot) => {
        setSelectedSlot(slot);
        setEditForm({
            startTime: slot.startTime,
            endTime: slot.endTime
        });
        setIsEditModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await TimeSlotServices.deleteTimeSlots();
            toast.success('Time slots deleted successfully');
            refreshTimeSlots();
        } catch (error) {
            toast.error(`Error deleting time slots: ${error.message}`);
        } finally {
            setIsDeleteModalOpen(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            await TimeSlotServices.updateTimeSlot(selectedSlot._id, editForm);
            toast.success('Time slot updated successfully');
            refreshTimeSlots();
            setIsEditModalOpen(false);
        } catch (error) {
            toast.error(`Error updating time slot: ${error.message}`);
        }
    };

    const handleCreateSlots = async (e) => {
        e.preventDefault();
        try {
            await TimeSlotServices.createTimeSlots(newSlotRange);
            toast.success('New time slots created successfully');
            setNewSlotRange({ startTime: '', endTime: '' });
            refreshTimeSlots();
        } catch (error) {
            toast.error(`Error creating time slots: ${error.message}`);
        }
    };

    if (isLoading) {
        return <div className="loader design_loading">Loading...</div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    return (
        <div className="right_col" role="main">
            <div className="d-md-flex align-items-center justify-content-between my-4 page-header-breadcrumb">
                <div className="my-auto">
                    <h5 className="page-title fs-21 mb-1">Time Slot Management List</h5>
                    <nav>
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                                <a href="javascript:void(0);">Home</a>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                Time Slot Management
                            </li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5 className="card-title">Manage Time Slots</h5>
                    <div className="card-options">
                        <button 
                            className="btn btn-primary me-2"
                            onClick={() => setIsDeleteModalOpen(true)}
                            disabled={!timeSlots || timeSlots.length === 0}
                        >
                            Delete All Slots
                        </button>
                    </div>
                </div>
                
                <div className="card-body">
                    <form onSubmit={handleCreateSlots} className="mb-4">
                        <div className="row">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label>Start Time (HH:mm)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="09:00"
                                        value={newSlotRange.startTime}
                                        onChange={(e) => setNewSlotRange({...newSlotRange, startTime: e.target.value})}
                                        pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label>End Time (HH:mm)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="18:00"
                                        value={newSlotRange.endTime}
                                        onChange={(e) => setNewSlotRange({...newSlotRange, endTime: e.target.value})}
                                        pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                                <button type="submit" className="btn btn-success">
                                    {timeSlots?.length > 0 ? 'Recreate All Slots' : 'Create Slots'}
                                </button>
                            </div>
                        </div>
                    </form>

                    <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                            <thead className="thead-light">
                                <tr>
                                    <th>#</th>
                                    <th>Start Time</th>
                                   
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTimeSlots.length > 0 ? (
                                    sortedTimeSlots.map((slot, index) => (
                                        <tr key={slot._id}>
                                            <td>{index + 1}</td>
                                            <td>{slot.startTime}</td>
                                            {/* <td>{slot.endTime}</td>
                                            <td>{`${slot.startTime} - ${slot.endTime}`}</td> */}
                                            <td>
                                                <button 
                                                    className="btn btn-sm btn-primary me-2"
                                                    onClick={() => handleEditClick(slot)}
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center">No time slots available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onRequestClose={() => setIsDeleteModalOpen(false)}
                className="modal-dialog-centered"
                overlayClassName="modal-backdrop"
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Confirm Deletion</h5>
                        <button type="button" className="close" onClick={() => setIsDeleteModalOpen(false)}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <p>Are you sure you want to delete all time slots? This action cannot be undone.</p>
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={() => setIsDeleteModalOpen(false)}
                        >
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-danger" 
                            onClick={handleDeleteConfirm}
                        >
                            Delete All
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Edit Time Slot Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onRequestClose={() => setIsEditModalOpen(false)}
                className="modal-dialog-centered"
                overlayClassName="modal-backdrop"
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Time Slot</h5>
                        <button type="button" className="close" onClick={() => setIsEditModalOpen(false)}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <form onSubmit={handleEditSubmit}>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Start Time (HH:mm)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={editForm.startTime}
                                    onChange={(e) => setEditForm({...editForm, startTime: e.target.value})}
                                    pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>End Time (HH:mm)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={editForm.endTime}
                                    onChange={(e) => setEditForm({...editForm, endTime: e.target.value})}
                                    pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                                    required
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={() => setIsEditModalOpen(false)}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
}

export default Timeslots;